/**
 * Graph navigation slice for the diagram store.
 *
 * Owns the actions that mutate the subgraph hierarchy: creating a group
 * from selected nodes, creating an empty group, dissolving a group back
 * into its parent graph, and entering/exiting graphs to navigate the tree.
 *
 * The slice is decoupled from the host store: callers provide an updater
 * function (which wraps Immer's `produce`) plus a state shape that
 * satisfies {@link GraphMutableState}.  Read-only helpers like
 * `getActiveGraph` stay in the host store since they don't need to mutate.
 */
import { generateId } from '../../utils';
import { calcNodeHeight, socketY } from '../../utils/socketLayout';
import { GROUP } from '../../constants/groups';
import type { Edge, Graph, Node, SocketDefinition } from '../../types';

const GROUP_BOUNDARY_NODE_WIDTH = GROUP.BOUNDARY_NODE_WIDTH;

// ── Default canvas / fit constants used by viewport centering ───────────
const DEFAULT_FIT_CANVAS_W = 1200;
const DEFAULT_FIT_CANVAS_H = 800;
const DEFAULT_FIT_PADDING = 80;

/** State fields the graph slice reads or mutates. */
export interface GraphMutableState {
  graphs: Record<string, Graph>;
  activeGraphId: string;
  graphNavStack: Array<{
    graphId: string;
    graphLabel: string;
    returnViewport: { x: number; y: number; scale: number };
  }>;
  nodes: Node[];
  edges: Edge[];
  viewport: { x: number; y: number; scale: number };
  selectedItems: string[];
}

/** Public action surface contributed by the graph slice. */
export interface GraphActions {
  createGroup: (nodeIds: string[], label?: string) => string | undefined;
  createEmptyGroup: (position: { x: number; y: number }, label?: string) => string;
  dissolveGroup: (groupNodeId: string) => void;
  enterGraph: (groupNodeId: string) => void;
  exitGraph: () => void;
}

/** Updater signature compatible with `set(produce(...))`. */
export type GraphStateUpdater<TState extends GraphMutableState> = (
  mutator: (state: TState) => void,
) => void;

/**
 * Compute a viewport that fits a node bounding box inside the default
 * canvas size (1200×800) with padding.  Returned scale is capped at 1 so
 * we never zoom in past 100%.
 */
function fitViewportToBounds(
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): { x: number; y: number; scale: number } {
  const boundsW = maxX - minX + 2 * DEFAULT_FIT_PADDING;
  const boundsH = maxY - minY + 2 * DEFAULT_FIT_PADDING;
  const fitScale = Math.min(
    DEFAULT_FIT_CANVAS_W / boundsW,
    DEFAULT_FIT_CANVAS_H / boundsH,
    1,
  );
  return {
    x: (DEFAULT_FIT_CANVAS_W - (maxX - minX) * fitScale) / 2 - minX * fitScale,
    y: (DEFAULT_FIT_CANVAS_H - (maxY - minY) * fitScale) / 2 - minY * fitScale,
    scale: fitScale,
  };
}

/**
 * Build the graph-navigation action set bound to the host store's updater.
 *
 * `applyUpdate` is expected to wrap Immer's `produce` so the supplied
 * mutator can mutate state directly while still returning a new immutable
 * snapshot upstream.
 */
export function createGraphActions<TState extends GraphMutableState>(
  applyUpdate: GraphStateUpdater<TState>,
): GraphActions {
  return {
    createGroup: (nodeIds, label) => {
      if (nodeIds.length < 2) return undefined;

      const childGraphId = generateId('graph');
      const groupNodeId = generateId('group-node');

      applyUpdate((state) => {
        const graph = state.graphs[state.activeGraphId];
        if (!graph) return;

        const nodesToMove = graph.nodes.filter((n) => nodeIds.includes(n.id));
        if (nodesToMove.length === 0) return;

        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        nodesToMove.forEach((n) => {
          minX = Math.min(minX, n.position.x);
          minY = Math.min(minY, n.position.y);
          maxX = Math.max(maxX, n.position.x + n.size.width);
          maxY = Math.max(maxY, n.position.y + n.size.height);
        });

        const nodeIdSet = new Set(nodeIds);
        const incomingEdges: Edge[] = [];
        const outgoingEdges: Edge[] = [];
        const internalEdges: Edge[] = [];

        graph.edges.forEach((e) => {
          const srcIn = nodeIdSet.has(e.sourceNodeId);
          const tgtIn = nodeIdSet.has(e.targetNodeId);
          if (srcIn && tgtIn) internalEdges.push(e);
          else if (!srcIn && tgtIn) incomingEdges.push(e);
          else if (srcIn && !tgtIn) outgoingEdges.push(e);
        });

        const inputSockets: SocketDefinition[] = [];
        const inputSocketMap = new Map<string, number>();
        incomingEdges.forEach((e) => {
          const key = `${e.targetNodeId}:${e.targetSocketId}`;
          if (!inputSocketMap.has(key)) {
            const idx = inputSockets.length;
            const targetNode = nodesToMove.find((n) => n.id === e.targetNodeId);
            const targetSocket = targetNode?.sockets.find((s) => s.id === e.targetSocketId);
            inputSockets.push({
              id: `gi-${idx}`,
              type: 'input',
              dataType: 'data',
              label: targetSocket?.label ?? `In ${idx + 1}`,
              position: { x: 0, y: socketY(idx) },
              order: idx,
            });
            inputSocketMap.set(key, idx);
          }
        });

        const outputSockets: SocketDefinition[] = [];
        const outputSocketMap = new Map<string, number>();
        outgoingEdges.forEach((e) => {
          const key = `${e.sourceNodeId}:${e.sourceSocketId}`;
          if (!outputSocketMap.has(key)) {
            const idx = outputSockets.length;
            const sourceNode = nodesToMove.find((n) => n.id === e.sourceNodeId);
            const sourceSocket = sourceNode?.sockets.find((s) => s.id === e.sourceSocketId);
            outputSockets.push({
              id: `go-${idx}`,
              type: 'output',
              dataType: 'data',
              label: sourceSocket?.label ?? `Out ${idx + 1}`,
              position: { x: 220, y: socketY(idx) },
              order: idx,
            });
            outputSocketMap.set(key, idx);
          }
        });

        const groupNode: Node = {
          id: groupNodeId,
          type: 'group',
          label: label ?? `Group ${Object.keys(state.graphs).length}`,
          position: { x: minX, y: minY },
          size: { width: 220, height: calcNodeHeight(inputSockets.length, outputSockets.length) },
          sockets: [...inputSockets, ...outputSockets],
          childGraphId,
          data: { color: '#64748b' },
        };

        const groupInputNode: Node | null =
          inputSockets.length > 0
            ? {
                id: generateId('gi'),
                type: 'group_input',
                label: 'Group Input',
                position: { x: minX - 200, y: (minY + maxY) / 2 - 50 },
                size: {
                  width: GROUP_BOUNDARY_NODE_WIDTH,
                  height: calcNodeHeight(0, inputSockets.length),
                },
                sockets: inputSockets.map((s, i) => ({
                  ...s,
                  id: `out-${i}`,
                  type: 'output' as const,
                  position: { x: GROUP_BOUNDARY_NODE_WIDTH, y: socketY(i) },
                })),
              }
            : null;

        const groupOutputNode: Node | null =
          outputSockets.length > 0
            ? {
                id: generateId('go'),
                type: 'group_output',
                label: 'Group Output',
                position: { x: maxX + 60, y: (minY + maxY) / 2 - 50 },
                size: {
                  width: GROUP_BOUNDARY_NODE_WIDTH,
                  height: calcNodeHeight(outputSockets.length, 0),
                },
                sockets: outputSockets.map((s, i) => ({
                  ...s,
                  id: `in-${i}`,
                  type: 'input' as const,
                  position: { x: 0, y: socketY(i) },
                })),
              }
            : null;

        const childNodes: Node[] = [...nodesToMove];
        if (groupInputNode) childNodes.push(groupInputNode);
        if (groupOutputNode) childNodes.push(groupOutputNode);

        const childEdges: Edge[] = [...internalEdges];

        if (groupInputNode) {
          incomingEdges.forEach((e) => {
            const key = `${e.targetNodeId}:${e.targetSocketId}`;
            const idx = inputSocketMap.get(key);
            if (idx !== undefined) {
              childEdges.push({
                id: generateId('edge'),
                sourceNodeId: groupInputNode.id,
                sourceSocketId: `out-${idx}`,
                targetNodeId: e.targetNodeId,
                targetSocketId: e.targetSocketId,
              });
            }
          });
        }

        if (groupOutputNode) {
          outgoingEdges.forEach((e) => {
            const key = `${e.sourceNodeId}:${e.sourceSocketId}`;
            const idx = outputSocketMap.get(key);
            if (idx !== undefined) {
              childEdges.push({
                id: generateId('edge'),
                sourceNodeId: e.sourceNodeId,
                sourceSocketId: e.sourceSocketId,
                targetNodeId: groupOutputNode.id,
                targetSocketId: `in-${idx}`,
              });
            }
          });
        }

        const childViewport = fitViewportToBounds(minX, minY, maxX, maxY);

        const childGraph: Graph = {
          id: childGraphId,
          nodes: childNodes,
          edges: childEdges,
          viewport: childViewport,
        };
        state.graphs[childGraphId] = childGraph;

        graph.nodes = graph.nodes.filter((n) => !nodeIds.includes(n.id));
        graph.nodes.push(groupNode);

        const edgeIdsToRemove = new Set([
          ...internalEdges.map((e) => e.id),
          ...incomingEdges.map((e) => e.id),
          ...outgoingEdges.map((e) => e.id),
        ]);
        graph.edges = graph.edges.filter((e) => !edgeIdsToRemove.has(e.id));

        incomingEdges.forEach((e) => {
          const key = `${e.targetNodeId}:${e.targetSocketId}`;
          const idx = inputSocketMap.get(key);
          if (idx !== undefined) {
            graph.edges.push({
              id: generateId('edge'),
              sourceNodeId: e.sourceNodeId,
              sourceSocketId: e.sourceSocketId,
              targetNodeId: groupNodeId,
              targetSocketId: `gi-${idx}`,
              label: e.label,
              animated: e.animated,
            });
          }
        });

        outgoingEdges.forEach((e) => {
          const key = `${e.sourceNodeId}:${e.sourceSocketId}`;
          const idx = outputSocketMap.get(key);
          if (idx !== undefined) {
            graph.edges.push({
              id: generateId('edge'),
              sourceNodeId: groupNodeId,
              sourceSocketId: `go-${idx}`,
              targetNodeId: e.targetNodeId,
              targetSocketId: e.targetSocketId,
              label: e.label,
              animated: e.animated,
            });
          }
        });

        state.nodes = graph.nodes;
        state.edges = graph.edges;
        state.selectedItems = [groupNodeId];
      });

      return groupNodeId;
    },

    createEmptyGroup: (position, label) => {
      const childGraphId = generateId('graph');
      const groupNodeId = generateId('group-node');

      applyUpdate((state) => {
        const graph = state.graphs[state.activeGraphId];
        if (!graph) return;

        const groupNode: Node = {
          id: groupNodeId,
          type: 'group',
          label: label ?? `Group ${Object.keys(state.graphs).length}`,
          position,
          size: { width: 180, height: 80 },
          sockets: [],
          childGraphId,
          data: { color: '#64748b' },
        };

        state.graphs[childGraphId] = {
          id: childGraphId,
          nodes: [],
          edges: [],
          viewport: { x: 0, y: 0, scale: 1 },
        };

        graph.nodes.push(groupNode);
        state.nodes = graph.nodes;
        state.edges = graph.edges;
        state.selectedItems = [groupNodeId];
      });

      return groupNodeId;
    },

    dissolveGroup: (groupNodeId) =>
      applyUpdate((state) => {
        const graph = state.graphs[state.activeGraphId];
        if (!graph) return;

        const groupNode = graph.nodes.find((n) => n.id === groupNodeId);
        if (!groupNode?.childGraphId) return;

        const childGraph = state.graphs[groupNode.childGraphId];
        if (!childGraph) return;

        const realNodes = childGraph.nodes.filter(
          (n) => n.type !== 'group_input' && n.type !== 'group_output',
        );

        const realNodeIds = new Set(realNodes.map((n) => n.id));
        const internalEdges = childGraph.edges.filter(
          (e) => realNodeIds.has(e.sourceNodeId) && realNodeIds.has(e.targetNodeId),
        );

        const edgesToGroup = graph.edges.filter((e) => e.targetNodeId === groupNodeId);
        const edgesFromGroup = graph.edges.filter((e) => e.sourceNodeId === groupNodeId);

        const giNode = childGraph.nodes.find((n) => n.type === 'group_input');
        const goNode = childGraph.nodes.find((n) => n.type === 'group_output');

        const restoredEdges: Edge[] = [];

        if (giNode) {
          edgesToGroup.forEach((parentEdge) => {
            const socketIdx = parentEdge.targetSocketId.replace('gi-', '');
            const childEdge = childGraph.edges.find(
              (e) => e.sourceNodeId === giNode.id && e.sourceSocketId === `out-${socketIdx}`,
            );
            if (childEdge) {
              restoredEdges.push({
                id: generateId('edge'),
                sourceNodeId: parentEdge.sourceNodeId,
                sourceSocketId: parentEdge.sourceSocketId,
                targetNodeId: childEdge.targetNodeId,
                targetSocketId: childEdge.targetSocketId,
                label: parentEdge.label,
                animated: parentEdge.animated,
              });
            }
          });
        }

        if (goNode) {
          edgesFromGroup.forEach((parentEdge) => {
            const socketIdx = parentEdge.sourceSocketId.replace('go-', '');
            const childEdge = childGraph.edges.find(
              (e) => e.targetNodeId === goNode.id && e.targetSocketId === `in-${socketIdx}`,
            );
            if (childEdge) {
              restoredEdges.push({
                id: generateId('edge'),
                sourceNodeId: childEdge.sourceNodeId,
                sourceSocketId: childEdge.sourceSocketId,
                targetNodeId: parentEdge.targetNodeId,
                targetSocketId: parentEdge.targetSocketId,
                label: parentEdge.label,
                animated: parentEdge.animated,
              });
            }
          });
        }

        graph.nodes = graph.nodes.filter((n) => n.id !== groupNodeId);
        graph.edges = graph.edges.filter(
          (e) => e.sourceNodeId !== groupNodeId && e.targetNodeId !== groupNodeId,
        );

        graph.nodes.push(...realNodes);
        graph.edges.push(...internalEdges, ...restoredEdges);

        delete state.graphs[groupNode.childGraphId];

        state.nodes = graph.nodes;
        state.edges = graph.edges;
        state.selectedItems = realNodes.map((n) => n.id);
      }),

    enterGraph: (groupNodeId) =>
      applyUpdate((state) => {
        const graph = state.graphs[state.activeGraphId];
        if (!graph) return;

        const groupNode = graph.nodes.find((n) => n.id === groupNodeId);
        if (!groupNode?.childGraphId) return;

        const childGraph = state.graphs[groupNode.childGraphId];
        if (!childGraph) return;

        state.graphNavStack.push({
          graphId: state.activeGraphId,
          graphLabel: state.activeGraphId === 'root' ? 'Root' : groupNode.label,
          returnViewport: { ...state.viewport },
        });

        let entryViewport = childGraph.viewport;
        const realNodes = childGraph.nodes.filter(
          (n) => n.type !== 'group_input' && n.type !== 'group_output',
        );
        const hasNodes = realNodes.length > 0;
        const isDefaultViewport =
          childGraph.viewport.x === 0 &&
          childGraph.viewport.y === 0 &&
          childGraph.viewport.scale === 1;
        if (hasNodes && isDefaultViewport) {
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
          childGraph.nodes.forEach((n) => {
            minX = Math.min(minX, n.position.x);
            minY = Math.min(minY, n.position.y);
            maxX = Math.max(maxX, n.position.x + n.size.width);
            maxY = Math.max(maxY, n.position.y + n.size.height);
          });
          entryViewport = fitViewportToBounds(minX, minY, maxX, maxY);
          childGraph.viewport = entryViewport;
        }

        state.activeGraphId = groupNode.childGraphId;
        state.nodes = childGraph.nodes;
        state.edges = childGraph.edges;
        state.viewport = entryViewport;
        state.selectedItems = [];
      }),

    exitGraph: () =>
      applyUpdate((state) => {
        if (state.graphNavStack.length === 0) return;

        const currentGraph = state.graphs[state.activeGraphId];
        if (currentGraph) {
          currentGraph.viewport = { ...state.viewport };
        }

        const entry = state.graphNavStack.pop()!;

        const parentGraph = state.graphs[entry.graphId];
        if (parentGraph) {
          state.activeGraphId = entry.graphId;
          state.nodes = parentGraph.nodes;
          state.edges = parentGraph.edges;
          state.viewport = entry.returnViewport;
          state.selectedItems = [];
        }
      }),
  };
}
